import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DescriptionIcon from '@mui/icons-material/Description';
import HotelIcon from '@mui/icons-material/Hotel';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import GroupsIcon from '@mui/icons-material/Groups';
import PublicIcon from '@mui/icons-material/Public';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import FactoryIcon from '@mui/icons-material/Factory';
import AnchorIcon from '@mui/icons-material/Anchor';
import ConstructionIcon from '@mui/icons-material/Construction';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AnimatedCounter from '../components/AnimatedCounter';
import AnimatedSection from '../components/AnimatedSection';
import { useToast } from '../contexts/ToastContext';
import { submitContactForm } from '../api/contact';

const serviceItems = [
  { title: 'International Flight Bookings', icon: FlightTakeoffIcon, description: 'Book reliable routes, flexible fares and efficient itineraries for business travellers.' },
  { title: 'Business Travel', icon: BusinessCenterIcon, description: 'Structured trip planning for meetings, site visits, conferences and regional travel.' },
  { title: 'Visa Assistance', icon: VerifiedUserIcon, description: 'Guidance for business visa requirements and document preparation.' },
  { title: 'Travel Documentation', icon: DescriptionIcon, description: 'Support with travel forms, approvals and company travel records.' },
  { title: 'Hotel Reservations', icon: HotelIcon, description: 'Well-located stays selected for convenience, comfort and productivity.' },
  { title: 'Travel Insurance', icon: BadgeIcon, description: 'Protect corporate travellers with suitable coverage options.' },
  { title: 'Flight Changes & Rebooking', icon: ChangeCircleIcon, description: 'Responsive assistance when plans change and timings need to move quickly.' },
  { title: 'Group Business Travel', icon: GroupsIcon, description: 'Coordinated support for teams, delegations and multi-traveller trips.' },
  { title: 'Dedicated Corporate Support', icon: SupportAgentIcon, description: 'A direct team contact for fast follow-up and dependable service.' }
];

const industries = [
  { title: 'Marine & Offshore', icon: AnchorIcon, description: 'Travel support for vessels, crews and offshore project schedules.' },
  { title: 'Shipping', icon: LocalShippingIcon, description: 'Reliable arrangements for port visits and crew movement.' },
  { title: 'Engineering', icon: ConstructionIcon, description: 'Travel coordination for technical teams and project deployments.' },
  { title: 'Construction', icon: FactoryIcon, description: 'Practical bookings for contractors, engineers and supervisors.' },
  { title: 'Manufacturing', icon: Inventory2Icon, description: 'Support for plant visits, inspections and regional business trips.' },
  { title: 'Oil & Gas', icon: OilBarrelIcon, description: 'Confident travel handling for time-sensitive energy sector operations.' },
  { title: 'Logistics', icon: PublicIcon, description: 'Travel coverage for supply chain teams and operational staff.' },
  { title: 'Corporate Organisations', icon: CorporateFareIcon, description: 'A dependable partner for internal travel programmes and executives.' }
];

const reasons = [
  '17 Years Experience',
  'Responsive Customer Support',
  'Competitive Corporate Rates',
  'Dedicated Travel Assistance',
  'Fast Turnaround',
  'Reliable Visa Support',
  'Personalised Service'
];

const processSteps = [
  { step: '1', title: 'Submit Request', description: 'Tell us your travel requirements, dates and destination.' },
  { step: '2', title: 'Receive Quotation', description: 'We prepare a practical and competitive corporate quote.' },
  { step: '3', title: 'Confirm Booking', description: 'Approve the plan and let us secure the arrangements.' },
  { step: '4', title: 'Travel With Confidence', description: 'Move forward with support from our corporate team.' }
];

const initialFormState = {
  companyName: '',
  contactPerson: '',
  email: '',
  phone: '',
  destination: '',
  travelDate: '',
  travellers: '',
  message: ''
};

const CorporateTravel = () => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const heroImage = 'https://images.unsplash.com/photo-1556388158-158ea5ccacbd?w=1600&h=1200&fit=crop&auto=format';

  const corporateTheme = useMemo(() => createTheme({
    typography: {
      fontFamily: "'Poppins', sans-serif"
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: "'Poppins', sans-serif",
            textTransform: 'none'
          }
        }
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: "'Poppins', sans-serif"
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: "'Poppins', sans-serif"
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            fontFamily: "'Poppins', sans-serif"
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            fontFamily: "'Poppins', sans-serif"
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined'
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: "'Poppins', sans-serif"
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontFamily: "'Poppins', sans-serif"
          }
        }
      }
    }
  }), []);

  useEffect(() => {
    document.title = 'Corporate Travel Services Singapore | AZ Holidays';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    const previousContent = metaDescription.getAttribute('content') || '';
    metaDescription.setAttribute('content', 'Corporate travel management in Singapore. International flight bookings, visa assistance, hotel reservations and dedicated business travel support backed by 17 years of experience.');

    return () => {
      metaDescription.setAttribute('content', previousContent);
    };
  }, []);

  const stats = useMemo(() => ([
    { value: 17, suffix: '+', label: 'Years of Experience' },
    { value: 24, suffix: '/7', label: 'Corporate Travel Support' },
    { value: 100, suffix: '+', label: 'Worldwide Destinations' }
  ]), []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      subject: 'Corporate Travel Quote Request',
      message: `Company: ${formData.companyName}\nContact Person: ${formData.contactPerson}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nDestination: ${formData.destination}\nTravel Date: ${formData.travelDate}\nNumber of Travellers: ${formData.travellers}\n\n${formData.message}`
    };

    const result = await submitContactForm(payload);
    setIsSubmitting(false);

    if (result.success) {
      showToast('Your corporate quote request has been sent. Our team will contact you shortly.', 'success');
      setFormData(initialFormState);
      return;
    }

    showToast(result.error || 'Unable to submit the request. Please try again.', 'error');
  };

  return (
    <ThemeProvider theme={corporateTheme}>
      <Box sx={{ bgcolor: '#f6f9fe', color: '#19304d', fontFamily: "'Poppins', sans-serif" }}>
      <Box sx={{ position: 'relative', overflow: 'hidden', color: '#fff', pt: { xs: 12, md: 14 }, pb: { xs: 10, md: 12 } }}>
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(135deg, rgba(11,38,68,0.86), rgba(28,88,162,0.7)), url(${heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center', transform: 'scale(1.02)' }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 18% 18%, rgba(255,255,255,0.18), transparent 28%), radial-gradient(circle at 82% 12%, rgba(255,255,255,0.12), transparent 22%)' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <AnimatedSection>
              <Box sx={{ maxWidth: 800, textAlign: 'center' }}>
                <Chip label="AZ Holidays Pte Ltd | Singapore | 17 Years of Experience" sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 600, px: 1.5, mb: 3, backdropFilter: 'blur(10px)' }} />
                <Typography variant="h1" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '2.5rem', sm: '3.4rem', md: '4.7rem' }, lineHeight: 1.04, mb: 2 }}>
                  Corporate Travel Solutions
                </Typography>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.8, maxWidth: 720, mx: 'auto', color: 'rgba(255,255,255,0.92)', mb: 4 }}>
                  Supporting businesses with reliable business travel management for over <strong>17 years</strong>. AZ Holidays provides international flight bookings, visa assistance, travel documentation, hotel reservations and dedicated support for businesses across Singapore.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                  <Button variant="contained" size="large" href="#quote-form" endIcon={<ArrowForwardIcon />} sx={{ bgcolor: '#fff', color: '#0f3a66', fontWeight: 700, px: 3.5, py: 1.6, borderRadius: 999, textTransform: 'none', boxShadow: '0 16px 40px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#f2f7ff' } }}>
                    Request a Corporate Quote
                  </Button>
                  <Button variant="outlined" size="large" href="/contact" sx={{ borderColor: 'rgba(255,255,255,0.55)', color: '#fff', fontWeight: 700, px: 3.5, py: 1.6, borderRadius: 999, textTransform: 'none', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                    Contact Us
                  </Button>
                </Stack>
              </Box>
            </AnimatedSection>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={3} justifyContent="center">
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <AnimatedSection delay={index * 0.05}>
                <Card elevation={0} sx={{ height: '100%', borderRadius: 4, border: '1px solid #e6eef8', boxShadow: '0 10px 30px rgba(20, 48, 77, 0.06)' }}>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '2.2rem', color: '#1e5aa5' }}>
                      <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                    </Typography>
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, color: '#3b4c62' }}>
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <AnimatedSection>
          <Typography variant="h2" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 1.5, color: '#16314f' }}>
            Services
          </Typography>
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#5d6f85', mb: 4, maxWidth: 760 }}>
            Built for companies that want clear communication, dependable arrangements and a single partner for business travel needs.
          </Typography>
        </AnimatedSection>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 3, alignItems: 'stretch', justifyItems: 'stretch', maxWidth: 1240, mx: 'auto' }}>
          {serviceItems.map((service, index) => {
            const Icon = service.icon;
            return (
              <AnimatedSection delay={index * 0.04} key={service.title} style={{ height: '100%' }}>
                <Card elevation={0} sx={{ height: '100%', minHeight: 176, borderRadius: 4, border: '1px solid #e5edf7', background: 'linear-gradient(180deg, #fff 0%, #fbfdff 100%)', boxShadow: '0 10px 28px rgba(16, 41, 67, 0.06)', transition: 'transform 0.3s ease, box-shadow 0.3s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 18px 40px rgba(16, 41, 67, 0.12)' } }}>
                  <CardContent sx={{ p: 3.2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'rgba(30, 90, 165, 0.08)', color: '#1e5aa5', mb: 2, flexShrink: 0 }}>
                      <Icon />
                    </Box>
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1.08rem', mb: 1, color: '#17314d', lineHeight: 1.35 }}>
                      {service.title}
                    </Typography>
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#5f6f84', lineHeight: 1.7, flexGrow: 1 }}>
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </AnimatedSection>
            );
          })}
        </Box>
      </Container>

      <Box sx={{ bgcolor: '#eef5fb', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <AnimatedSection>
            <Typography variant="h2" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 1.5, color: '#16314f' }}>
              Industries We Support
            </Typography>
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#5d6f85', mb: 4, maxWidth: 760 }}>
              Our corporate travel support extends across sectors that value speed, precision and dependable execution.
            </Typography>
          </AnimatedSection>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 3, alignItems: 'stretch', justifyItems: 'stretch', maxWidth: 1240, mx: 'auto' }}>
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <AnimatedSection delay={index * 0.04} key={industry.title} style={{ height: '100%' }}>
                  <Card elevation={0} sx={{ height: '100%', minHeight: 170, borderRadius: 4, border: '1px solid #dfe9f4', boxShadow: '0 10px 28px rgba(16, 41, 67, 0.05)' }}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Icon sx={{ color: '#1e5aa5', fontSize: 34, mb: 2, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, mb: 1, color: '#17314d', lineHeight: 1.35 }}>{industry.title}</Typography>
                      <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#5f6f84', lineHeight: 1.7, flexGrow: 1 }}>{industry.description}</Typography>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              );
            })}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4} alignItems="stretch" justifyContent="center">
          <Grid item xs={12} md={5.8}>
            <AnimatedSection>
              <Typography variant="h2" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 1.5, color: '#16314f' }}>
                Why Choose AZ Holidays
              </Typography>
              <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#5d6f85', mb: 3 }}>
                A corporate partner should reduce friction, not create it. We focus on response speed, professionalism and practical support.
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                {reasons.map((reason, index) => (
                  <Grid item xs={12} sm={6} key={reason}>
                    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ delay: index * 0.05 }}>
                      <Paper elevation={0} sx={{ p: 2.2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.8)', border: '1px solid #dde8f3', backdropFilter: 'blur(10px)', boxShadow: '0 10px 24px rgba(16, 41, 67, 0.05)' }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, color: '#17314d' }}>{reason}</Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </AnimatedSection>
          </Grid>
          <Grid item xs={12} md={6.2}>
            <AnimatedSection delay={0.08}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 5, bgcolor: '#fff', border: '1px solid #dfe9f4', boxShadow: '0 16px 40px rgba(16, 41, 67, 0.07)' }}>
                <Typography variant="h5" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: '#17314d', mb: 3 }}>
                  How we work
                </Typography>
                <Box sx={{ position: 'relative', pl: 1 }}>
                  {processSteps.map((step, index) => (
                    <Box key={step.step} sx={{ display: 'flex', gap: 2.5, position: 'relative', pb: index < processSteps.length - 1 ? 3 : 0 }}>
                      <Box sx={{ width: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#1e5aa5', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontFamily: "'Poppins', sans-serif", boxShadow: '0 10px 20px rgba(30, 90, 165, 0.25)' }}>
                          {step.step}
                        </Box>
                        {index < processSteps.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 36, bgcolor: 'linear-gradient(180deg, #1e5aa5, #9ec3ef)' }} />}
                      </Box>
                      <Box sx={{ pb: 1 }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: '#17314d', mb: 0.5 }}>{step.title}</Typography>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#5f6f84', lineHeight: 1.7 }}>{step.description}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </AnimatedSection>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" id="quote-form" sx={{ py: { xs: 6, md: 8 } }}>
        <AnimatedSection>
          <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 4 }}>
            <Typography variant="h2" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 1.5, color: '#16314f' }}>
              Request a Corporate Quote
            </Typography>
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#5d6f85', lineHeight: 1.8 }}>
              Share your company travel needs and our team will respond with a practical quotation and next steps.
            </Typography>
          </Box>
        </AnimatedSection>
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          <Grid item xs={12} md={10}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 5, bgcolor: '#fff', border: '1px solid #dfe9f4', boxShadow: '0 16px 40px rgba(16, 41, 67, 0.08)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <TextField sx={{ flex: '1 1 220px' }} label="Company Name" name="companyName" value={formData.companyName} onChange={handleChange} required />
                  <TextField sx={{ flex: '1 1 220px' }} label="Contact Person" name="contactPerson" value={formData.contactPerson} onChange={handleChange} required />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <TextField sx={{ flex: '1 1 220px' }} label="Email" type="email" name="email" value={formData.email} onChange={handleChange} required />
                  <TextField sx={{ flex: '1 1 220px' }} label="Phone" name="phone" value={formData.phone} onChange={handleChange} />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <TextField sx={{ flex: '1 1 220px' }} label="Destination" name="destination" value={formData.destination} onChange={handleChange} required />
                  <TextField sx={{ flex: '1 1 220px' }} label="Travel Date" name="travelDate" type="date" value={formData.travelDate} onChange={handleChange} InputLabelProps={{ shrink: true }} required />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <TextField sx={{ flex: '1 1 220px' }} select label="Number of Travellers" name="travellers" value={formData.travellers} onChange={handleChange} required>
                    {['1', '2-5', '6-10', '11-20', '20+'].map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                  </TextField>
                  <Box sx={{ flex: '2 1 300px' }}>
                    <TextField fullWidth label="Message" name="message" value={formData.message} onChange={handleChange} multiline minRows={4} required />
                  </Box>
                </Box>
                <Alert severity="info" sx={{ borderRadius: 3 }}>Your quote request will be sent to AZ Holidays for a corporate follow-up.</Alert>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Button type="submit" onClick={handleSubmit} variant="contained" size="large" disabled={isSubmitting} sx={{ bgcolor: '#1e5aa5', fontWeight: 700, px: 4, py: 1.5, borderRadius: 999, textTransform: 'none', '&:hover': { bgcolor: '#174a88' } }}>
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ py: { xs: 6, md: 8 }, background: 'linear-gradient(135deg, #0f3a66 0%, #1e5aa5 100%)', color: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '2rem', md: '2.8rem' }, mb: 1.5 }}>
                Ready to simplify your company's travel?
              </Typography>
              <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: 'rgba(255,255,255,0.9)', fontSize: { xs: '1rem', md: '1.08rem' } }}>
                Request a quotation today.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button variant="contained" href="/contact" size="large" sx={{ bgcolor: '#fff', color: '#0f3a66', fontWeight: 700, px: 4, py: 1.5, borderRadius: 999, textTransform: 'none', '&:hover': { bgcolor: '#f2f7ff' } }}>
                Contact Our Corporate Team
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      </Box>
    </ThemeProvider>
  );
};

export default CorporateTravel;